import { Box, Button, Grid, HStack, Link, Text, VStack } from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { PenIcon } from 'lucide-react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { djs, formatShortDate } from '@duke-hq/djs';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { testIds } from './testIds';
import type { PlacementDetailsState, PlacementContract } from './types';

function Field({
  label,
  value,
  span,
}: {
  label: React.ReactNode;
  value: string | null | undefined;
  span?: boolean;
}): React.ReactElement {
  return (
    <Box gridColumn={span ? '1 / -1' : undefined}>
      <Text fontSize="sm" color="fg.muted">
        {label}
      </Text>
      <Text noOfLines={1}>{value || '—'}</Text>
    </Box>
  );
}

export function Summary({
  student,
  contract,
  details,
  onEdit,
}: {
  student: { fullName: string; courseLabel: string; usi: string | null };
  contract: PlacementContract;
  details: PlacementDetailsState;
  onEdit: () => void;
}): React.ReactElement {
  const { employer, employerContact, documentUrl } = contract;
  const { data: employerDetail, isError } =
    trpcAdmin.workPlacements.employers.getEmployerDetail.useQuery({
      employerId: employer.id,
    });

  const contact = employerDetail?.contacts.find(
    (candidate) => candidate.id === employerContact?.id,
  );

  return (
    <VStack align="stretch" gap={5} w="full">
      <Box>
        <Text fontWeight="bold" mb={2}>
          <Trans>Student details</Trans>
        </Text>
        <Grid templateColumns="1fr 1fr" gap={3}>
          <Field label={<Trans>Name</Trans>} value={student.fullName} />
          <Field label={<Trans>USI</Trans>} value={student.usi} />
          <Field label={<Trans>Course</Trans>} value={student.courseLabel} span />
        </Grid>
      </Box>

      <Box>
        <HStack justify="space-between" mb={2}>
          <Text fontWeight="bold">
            <Trans>Placement details</Trans>
          </Text>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<PenIcon size={iconSize.lg} />}
            onClick={onEdit}
            data-testid={testIds.editSummary}
          >
            <Trans>Edit</Trans>
          </Button>
        </HStack>
        {isError ? (
          <Text fontSize="sm" color="danger" mb={2}>
            <Trans>Unable to load the employer's address and contact details.</Trans>
          </Text>
        ) : null}
        <Grid templateColumns="1fr 1fr" gap={3}>
          <Field
            label={<Trans>Start date</Trans>}
            value={formatShortDate(details.startedAt ? djs(details.startedAt).toDate() : null)}
          />
          <Field label={<Trans>Organisation</Trans>} value={employer.name} />
          <Field label={<Trans>Street address</Trans>} value={employerDetail?.formattedAddress} />
          <Box>
            <Text fontSize="sm" color="fg.muted">
              <Trans>Contract</Trans>
            </Text>
            {documentUrl != null ? (
              <Link
                href={documentUrl}
                isExternal
                color="fg.accent.emphasis"
                fontWeight="medium"
                data-testid={testIds.summaryContractLink}
              >
                <Trans>View contract</Trans>
              </Link>
            ) : (
              <Text>—</Text>
            )}
          </Box>
          {details.agreement != null ? (
            <Field label={<Trans>Workplace agreement</Trans>} value={details.agreement.filename} />
          ) : null}
          <Field
            label={<Trans>Contact</Trans>}
            value={
              employerContact != null
                ? `${employerContact.firstName} ${employerContact.lastName}`
                : null
            }
          />
          <Field label={<Trans>Phone</Trans>} value={contact?.phone} />
          <Field label={<Trans>Email</Trans>} value={contact?.email} />
        </Grid>
        {details.notes.trim() !== '' ? (
          <Box mt={3}>
            <Text fontSize="sm" color="fg.muted">
              <Trans>Notes</Trans>
            </Text>
            <Text whiteSpace="pre-wrap">{details.notes}</Text>
          </Box>
        ) : null}
      </Box>
    </VStack>
  );
}
